/**
 * DownloadableChartCard — universal admin chart wrapper.
 * Adds a thin header bar with the chart title + PNG/JPEG download buttons,
 * captures whatever DOM sits inside it (SVG + HTML + tables all work).
 *
 * Use it anywhere in `/admin/*` that renders a chart. Keep titles concise and
 * pass a unique `filenameBase` so downloads don't clobber each other.
 *
 * Also exports `<DownloadButtons />` — drop-in PNG/JPG button group that
 * targets any ref. Use this when you want to keep an existing card header
 * and just add the download controls in place.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { toPng, toJpeg } from "html-to-image";
import { Download } from "@phosphor-icons/react";

const slug = (s) =>
  String(s || "chart").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

async function _downloadFromRef(node, fmt, filenameBase) {
  const opts = { cacheBust: true, pixelRatio: 2, backgroundColor: "#ffffff" };
  const dataUrl = fmt === "jpeg"
    ? await toJpeg(node, { ...opts, quality: 0.95 })
    : await toPng(node, opts);
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = `${slug(filenameBase)}.${fmt === "jpeg" ? "jpg" : "png"}`;
  a.click();
}

export function DownloadButtons({ targetRef, filenameBase, size = "sm", className = "" }) {
  const [busy, setBusy] = useState(false);
  const click = async (fmt) => {
    if (!targetRef?.current) {
      toast.error("Nothing to download");
      return;
    }
    setBusy(true);
    try {
      await _downloadFromRef(targetRef.current, fmt, filenameBase || "chart");
      toast.success(`Downloaded as ${fmt.toUpperCase()}`);
    } catch (_err) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };
  const tid = slug(filenameBase || "chart");
  const btnCls = size === "xs"
    ? "text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-neutral-200 hover:bg-neutral-100 disabled:opacity-50 inline-flex items-center gap-1"
    : "text-[10px] uppercase tracking-wider px-2 py-1 border border-neutral-200 hover:bg-neutral-100 disabled:opacity-50 inline-flex items-center gap-1";
  return (
    <div className={`flex gap-1 shrink-0 ${className}`}>
      <button type="button" disabled={busy} onClick={() => click("png")} className={btnCls} title="Download PNG" data-testid={`chart-download-png-${tid}`}>
        <Download size={12} weight="bold" /> PNG
      </button>
      <button type="button" disabled={busy} onClick={() => click("jpeg")} className={btnCls} title="Download JPEG" data-testid={`chart-download-jpg-${tid}`}>
        <Download size={12} weight="bold" /> JPG
      </button>
    </div>
  );
}

export default function DownloadableChartCard({
  title,
  subtitle,
  children,
  filenameBase,
  className = "",
  bodyClassName = "p-4",
  // Set false to skip the wrapper border/header (useful inside other cards)
  bordered = true,
}) {
  const ref = useRef(null);
  const tid = slug(title);
  return (
    <div className={`${bordered ? "border border-neutral-200 bg-white" : ""} ${className}`} data-testid={`chart-card-${tid}`}>
      <div className="flex items-start justify-between px-4 py-3 border-b border-neutral-100 gap-2">
        <div className="min-w-0 flex-1">
          {/* Title wraps across multiple lines — never truncated. Questions
              (especially the full text of a survey question) are shown in
              full so the chart is unambiguous. */}
          <div className="text-sm font-bold tracking-tight leading-snug">{title}</div>
          {subtitle && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{subtitle}</div>}
        </div>
        <DownloadButtons targetRef={ref} filenameBase={filenameBase || title} />
      </div>
      <div ref={ref} className={bodyClassName} style={{ background: "#fff" }}>
        {children}
      </div>
    </div>
  );
}

